<template>
    <div class="overview-focus w-100">
        <AonCard :title="currentSelection" :desc="currentSelectionDescription">
            <template #header-append>
                <ArmoryOptionToggle
                    class="ml-lg mt-sm"
                    style="min-width: 250px"
                    v-model="toggleValue"
                    :options="[
                        { label: 'By Coverage', value: 'coverage' },
                        { label: 'By Patent Count', value: 'patentCount' },
                    ]"
                    size="sm"
                    @update:modelValue="onToggleChange"
                />
            </template>
            <div class="card-content">
                <FocusChart
                    chart-type="treemap"
                    :chart-data="dataToUse"
                    :chart-overwrite-data="overwriteData"
                    :toggle-value="toggleValue"
                />
                <FocusTable
                    class="mt-md"
                    :key="`table-${toggleValue}`"
                    :table-data="dataToUse"
                    @filter-change="onFilterChange"
                    :default-sort="[
                        {
                            colId: toggleValue === 'coverage' ? 'innovationScore' : 'patentCount',
                            sort: 'desc',
                        },
                    ]"
                />
            </div>
        </AonCard>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useEntityStore } from '@/stores'

import FocusChart from './shared/FocusChart.vue'
import FocusTable from './shared/FocusTable.vue'

const entityStore = useEntityStore()

const overwriteData = ref([])
const toggleValue = ref('coverage')

const props = defineProps({
    productData: {
        type: Array,
        required: true,
    },
    technologyData: {
        type: Array,
        required: true,
    },
})

const dataToUse = computed(() => {
    return entityStore.strategyTabNodeFilter === 'product'
        ? props.productData
        : props.technologyData
})

const currentSelection = computed(() => {
    return toggleValue.value === 'coverage' ? 'Coverage' : 'Patent Count'
})

const currentSelectionDescription = computed(() => {
    if (entityStore.strategyTabNodeFilter === 'product') {
        return toggleValue.value === 'coverage'
            ? `This chart shows ${entityStore.entity.name}'s participation across different product classes based on patent holdings. Each bar represents a product area. Bar length reflects relative coverage, and color intensity indicates the Market Appeal ratio, with darker shades representing higher appeal.`
            : `This chart displays ${entityStore.entity.name}'s patent distribution across product classes. Each bar represents a product area. Bar length corresponds to the number of patents, while color intensity shows the Market Appeal ratio, with darker colors indicating stronger appeal.`
    } else {
        return toggleValue.value === 'coverage'
            ? `This chart shows ${entityStore.entity.name}'s participation across different technology and product classes based on patent holdings. Each bar represents a technology area. Bar length reflects the relative coverage, and color intensity represents the Market Appeal ratio, with darker colors indicating higher appeal.`
            : `This chart displays ${entityStore.entity.name}'s patent distribution across technology and product classes. Each bar represents a technology area. Bar length corresponds to patent count, and color intensity indicates Market Appeal, with darker shades showing greater appeal.`
    }
})

const onToggleChange = (value) => {
    toggleValue.value = value

    if (dataToUse.value.length > 0) {
        onFilterChange(dataToUse.value)
    }
}

watch(toggleValue, (newValue) => {
    if (dataToUse.value.length > 0) {
        onFilterChange(dataToUse.value)
    }
})

const onFilterChange = (data) => {
    overwriteData.value = data
}
</script>

<style lang="scss" scoped>
.overview-focus {
    position: relative;
}
</style>
